import {CheckboxControl, PanelBody, SelectControl} from '@wordpress/components';
import {__, sprintf} from '@wordpress/i18n';
import {Type} from '@wordpress/api/types';
import {Taxonomy} from '@wordpress/api/taxonomies';
import type {Attr} from './pages/Edit';
import DOMPurify from 'dompurify';
import {PanelColorSettings} from '@wordpress/block-editor';
import {clone, noop} from 'lodash';
import {keysOf} from '../../globals/helpers';

import styles from './accordion.pcss';

export type Initialize = {
	values: AccordionOptions;
	clientId: string;
}

type Levels = {
	all: boolean;
	highest: boolean;
	child: boolean;
	grandchild: boolean;
}

export type IconType = 'arrow' | 'hamburger' | 'plus' | 'solid_arrow';

export type AccordionOptions = {
	enable_accordion: boolean;
	accordion_style?: '' | IconType;
	icon_color?: string;
	icon_hover_color?: string;
	keep_closed: boolean;
	include_parent_in_accordion: boolean;
	links_expand: boolean;
	links_expand_levels?: Partial<Levels>;
}

type SetAttributes = ( newValue: {
	[attribute in keyof AccordionOptions]?: AccordionOptions[attribute]
} ) => void;

/**
 * Convert a boolean to "checked" or false to match the value expected by the accordion JS.
 */
export type ToChecked<T> = T extends boolean ? 'checked' | false : T;

/**
 * Format of options passed to the accordion JS.
 */
export type AccordionDataFormat = {
	[key in keyof AccordionOptions]: ToChecked<AccordionOptions[key]>;
} & {
	// Used in the accordions from other block fields.
	include_parent: 'checked' | false;
	links_expand_levels: Array<keyof Levels>;
}

type Props = {
	attributes: AccordionOptions;
	setAttributes: SetAttributes;
	type?: Type<'edit'> | Taxonomy<'edit'>;
};

const icons = window.ADVANCED_SIDEBAR_MENU_ACCORDIONS.icons;
const sanitize = DOMPurify.sanitize;

const DEFAULT_VALUES: Required<Pick<Attr, 'include_parent'>> = {
	// Must be checked to allow accordion and block styles to work properly.
	include_parent: true,
};

/**
 * Load accordion functionality within the Gutenberg editor.
 *
 * If an accordion was previously added and is no longer enabled
 * we remove it from `enabled_accordions` before running `init`.
 */
export const initializeAccordions = ( {
	values,
	clientId,
}: Initialize ): void => {
	if ( 'undefined' === typeof window.advancedSidebarMenuPro ) {
		return;
	}

	window.advancedSidebarMenuPro.accordions.enabled[ `${clientId}-wrap` ] = convertDataToAccordionFormat( values );

	// Within an iframe like FSE, so we pass the `el` to target.
	const iframe = $( '[name="editor-canvas"]' ).contents().find( '[data-js="' + clientId + '-wrap"]' ) as JQuery<HTMLDivElement>;
	if ( iframe.length !== 0 ) {
		window.advancedSidebarMenuPro.accordions.enabled[ `${clientId}-wrap` ].el = iframe;
	}

	// This accordion is disabled so remove it from enabled before init.
	if ( ! values.enable_accordion ) {
		delete window.advancedSidebarMenuPro.accordions.enabled[ `${clientId}-wrap` ];
	}

	// Within the customizer, the widget id gets added to enabled_accordions we just
	// need to call the `init` inside the iframe.
	const customizer = $( '#customize-preview > iframe' ).get( 0 ) as HTMLIFrameElement;
	if ( 'undefined' !== typeof customizer && values.enable_accordion ) {
		customizer.contentWindow?.advancedSidebarMenuPro?.accordions.init( true );
	}
	window.advancedSidebarMenuPro.accordions.init( true );
};

/**
 * The accordion expects data to already be massaged to match
 * its format.
 *
 * 1. Checked values are "checked".
 * 2. Levels of links for open/close are an array.
 *
 */
export const convertDataToAccordionFormat = ( attributes: AccordionOptions ): AccordionDataFormat => {
	const settings: AccordionOptions & typeof DEFAULT_VALUES = {...DEFAULT_VALUES, ...clone<AccordionOptions>( attributes )};

	const config: AccordionDataFormat = {
		enable_accordion: settings.enable_accordion ? 'checked' : false,
		accordion_style: settings.accordion_style,
		keep_closed: settings.keep_closed ? 'checked' : false,
		include_parent_in_accordion: settings.include_parent_in_accordion ? 'checked' : false,
		links_expand: settings.links_expand ? 'checked' : false,
		include_parent: settings.include_parent ? 'checked' : false,
		links_expand_levels: keysOf( settings.links_expand_levels ?? {} ).filter( ( key: keyof Levels ): boolean => {
			return true === settings.links_expand_levels?.[ key ];
		} ) as Array<keyof Levels>,
	};
	if ( undefined !== settings.icon_color ) {
		config.icon_color = settings.icon_color;
	}
	if ( undefined !== settings.icon_hover_color ) {
		config.icon_hover_color = settings.icon_hover_color;
	}

	return config;
};

/**
 * Get the help message for the highest level parent include.
 *
 * Provides user feedback when the highest level parent is not included
 * in the generated menu.
 */
function getHelpMessageForIncludeHighestLevel( attributes: AccordionOptions & {
	include_parent?: boolean
}, typeLabel: string ): undefined | string {
	if ( true !== attributes.include_parent ) {
		/* translators: %s: Selected post type plural label */
		return sprintf( __( 'The highest level %s are not currently included in the menu.', 'advanced-sidebar-menu' ), typeLabel );
	}

	return __( 'If checked, the highest level parent will become part of the accordion open/close ability.', 'advanced-sidebar-menu' );
}


/**
 * Get the help message for the highest level parent link level.
 *
 * Provides user feedback when the highest level parent is not included
 * in the generated menu or accordion.
 */
function getHelpMessageForHighestLevel( attributes: AccordionOptions & {
	include_parent?: boolean
}, typeLabel: string ): undefined | string {
	if ( ! attributes.include_parent_in_accordion ) {
		return __( 'The highest level parent is not currently included in the accordion.', 'advanced-sidebar-menu' );
	}

	if ( true !== attributes.include_parent ) {
		/* translators: %s: Selected post type plural label */
		return sprintf( __( 'The highest level %s are not currently included in the menu.', 'advanced-sidebar-menu' ), typeLabel );
	}

	return undefined;
}

/**
 * Accordion settings in the editor.
 *
 * @function Object() { [native code] }
 */
const Accordion = ( {attributes, setAttributes, type}: Props ) => {
	const icon = ( 'undefined' === typeof attributes.accordion_style || '' === attributes.accordion_style ) ? 'plus' : attributes.accordion_style;
	const levels: Levels = Object.assign( {
		all: false,
		highest: false,
		child: false,
		grandchild: false,
	}, attributes.links_expand_levels ?? {} );


	const SetExpandLevel = ( key: keyof Levels ) => ( checked: boolean ) => {
		if ( ! checked ) {
			delete levels[ key ];
		} else {
			levels[ key ] = true;
		}
		setAttributes( {
			links_expand_levels: levels,
		} );
	};

	const typeLabel = type?.labels?.name.toLowerCase() ?? __( 'links', 'advanced-sidebar-menu' );

	return (
		<PanelBody
			title={__( 'Accordion', 'advanced-sidebar-menu' )}
			initialOpen={attributes.enable_accordion}
		>
			<CheckboxControl
				label={__( 'Enable accordion menu', 'advanced-sidebar-menu' )}
				/* translators: %s: Selected post type plural label */
				help={type?.labels && sprintf( __( 'Commonly used with "Always display child %s."', 'advanced-sidebar-menu' ), typeLabel )}
				checked={attributes.enable_accordion}
				onChange={value => {
					setAttributes( {
						enable_accordion: value,
					} );
				}}
				// @ts-expect-error -- Not technically available until WP 6.7.
				__nextHasNoMarginBottom
			/>
			{attributes.enable_accordion && <>
				<div className={styles.style}>
					<SelectControl<IconType>
						value={icon}
						label={__( 'Icon style', 'advanced-sidebar-menu' )}
						labelPosition={'side'}
						options={keysOf( icons ).map( ( option: IconType ) => ( {
							label: icons[ option ].label,
							value: option,
						} ) )}
						onChange={value => {
							setAttributes( {
								accordion_style: value,
							} );
						}}
					/>
					<span className={styles.icons} style={{color: attributes.icon_color}}>
						<span dangerouslySetInnerHTML={{__html: sanitize( icons[ icon ].contract )}} />
						<span dangerouslySetInnerHTML={{__html: sanitize( icons[ icon ].expand )}} />
					</span>
				</div>
				<PanelColorSettings
					className={styles.color}
					colorSettings={[
						{
							value: attributes.icon_color,
							onChange: color => setAttributes( {
								icon_color: color,
							} ),
							label: __( 'Icon color', 'advanced-sidebar-menu' ),
						}, {
							value: attributes.icon_hover_color,
							onChange: color => setAttributes( {
								icon_hover_color: color,
							} ),
							label: __( 'Icon hover color', 'advanced-sidebar-menu' ),
						},
					]}
				/>
				<CheckboxControl
					label={__( 'Close all accordion sections until clicked', 'advanced-sidebar-menu' )}
					help={__( 'If unchecked, the menu will open to the current section automatically.', 'advanced-sidebar-menu' )}
					checked={attributes.keep_closed}
					onChange={value => {
						setAttributes( {
							keep_closed: value,
						} );
					}}
					// @ts-expect-error -- Not technically available until WP 6.7.
					__nextHasNoMarginBottom
				/>
				<CheckboxControl
					label={__( 'Include the highest level parent in accordion', 'advanced-sidebar-menu' )}
					help={getHelpMessageForIncludeHighestLevel( attributes, typeLabel )}
					checked={attributes.include_parent_in_accordion}
					onChange={value => {
						setAttributes( {
							include_parent_in_accordion: value,
						} );
					}}
					// @ts-expect-error -- Not technically available until WP 6.7.
					__nextHasNoMarginBottom
				/>
				<CheckboxControl
					label={__( 'Use links for open/close', 'advanced-sidebar-menu' )}
					help={__( 'If checked, links of the selected levels will open and close their section when clicked.', 'advanced-sidebar-menu' )}
					checked={attributes.links_expand}
					onChange={checked => {
						setAttributes( {
							links_expand: checked,
						} );
					}}
					// @ts-expect-error -- Not technically available until WP 6.7.
					__nextHasNoMarginBottom
				/>

				{attributes.links_expand &&
					<div className={styles.levels} id={'accordion-levels'}>
						{/** Using a mock checkbox here to ensure consistent styles. */}
						<CheckboxControl
							className={styles.noCheck}
							checked={false}
							onChange={noop}
							label={__( 'Levels of links to use for open/close', 'advanced-sidebar-menu' )}
							help={__( 'Unchecked levels will only open and close when the icons are clicked.', 'advanced-sidebar-menu' )}
							// @ts-expect-error -- Not technically available until WP 6.7.
							__nextHasNoMarginBottom
						/>

						<CheckboxControl
							label={__( 'All levels', 'advanced-sidebar-menu' )}
							checked={levels.all}
							onChange={SetExpandLevel( 'all' )}
							// @ts-expect-error -- Not technically available until WP 6.7.
							__nextHasNoMarginBottom
						/>
						{! levels.all && <>
							<CheckboxControl
								/* translators: %s: Selected post type plural label */
								label={sprintf( __( 'Highest level parent %s', 'advanced-sidebar-menu' ), typeLabel )}
								checked={levels.highest}
								onChange={SetExpandLevel( 'highest' )}
								disabled={undefined !== getHelpMessageForHighestLevel( attributes, typeLabel )}
								help={getHelpMessageForHighestLevel( attributes, typeLabel )}
								// @ts-expect-error -- Not technically available until WP 6.7.
								__nextHasNoMarginBottom
							/>
							<CheckboxControl
								/* translators: %s: Selected post type plural label */
								label={sprintf( __( 'Child %s', 'advanced-sidebar-menu' ), typeLabel )}
								checked={levels.child}
								onChange={SetExpandLevel( 'child' )}
								// @ts-expect-error -- Not technically available until WP 6.7.
								__nextHasNoMarginBottom
							/>
							<CheckboxControl
								/* translators: %s: Selected post type plural label */
								label={sprintf( __( 'Grandchild %s', 'advanced-sidebar-menu' ), typeLabel )}
								checked={levels.grandchild}
								onChange={SetExpandLevel( 'grandchild' )}
								// @ts-expect-error -- Not technically available until WP 6.7.
								__nextHasNoMarginBottom
							/>
						</>}
					</div>}
			</>}
		</PanelBody>
	);
};

export default Accordion;
